/* composer-configurator.js v26 — DecoNova composer (vanilla JS).
   v26: removes the canvas hue replacement (didn't work on photoreal). Adds the
   PrintForge live-gen flow: when a combo isn't in visuals_data, asks PrintForge
   to generate via Gemini + upload to Shopify Files + return URL. While waiting,
   shows the SVG fallback with a small "generating" badge. Caches results in
   sessionStorage so the same visitor never re-asks.

   Fallback chain (in priority order):
     1. exact photo from visuals_data (the warm cache, manually maintained)
     2. sessionStorage cache (this browser session)
     3. SVG fallback rendered immediately + async POST to PrintForge
        → if PrintForge returns a URL within ~15s, swap SVG to photo
        → if not, leave SVG (the next visitor benefits when gen completes)
*/
(function(){
  var root = document.querySelector('[data-cfg-root]');
  if (!root) return;
  var initEl = root.querySelector('[data-cfg-init]');
  if (!initEl) return;
  var DATA;
  try { DATA = JSON.parse(initEl.textContent || initEl.innerText); }
  catch(e){ console.error('[cfg] init parse failed', e); return; }

  var FORMATS = DATA.formats || [];
  var CONSTRUCTIONS = DATA.constructions || [];
  var COLORS = DATA.colors || [];

  // visuals_data accepts two schemas — short ({f,t,b,img}) and long
  // ({format,top_key,bot_key,shopify_url}). Normalize to short here so the
  // rest of the code only deals with one shape. Drops entries missing any
  // required field. Also strips the `?v=...` cache-busting query string from
  // CDN URLs since Shopify serves the file without it just fine.
  var VISUALS = (DATA.visuals || []).map(function(v){
    var img = v.img || v.shopify_url || '';
    if (img && img.indexOf('?') !== -1) img = img.split('?')[0];
    return { f: v.f || v.format, t: v.t || v.top_key, b: v.b || v.bot_key, img: img };
  }).filter(function(v){ return v.f && v.t && v.b && v.img; });
  var CDN = DATA.cdnBase || 'https://cdn.shopify.com/';
  var CTA_INCOMPLETE = DATA.ctaIncomplete || 'Terminez votre composition';
  var CTA_COMPLETE = DATA.ctaComplete || 'Ajouter au panier — %price%';
  var ANIM = DATA.animations || 'subtle';
  var SHOW_PULSE = DATA.showPulse !== false;

  // PrintForge live-gen endpoint (base URL like https://host/api/deconova).
  // If empty, live-gen is disabled and the configurator stays on SVG fallback.
  var PF_BASE = (DATA.printforgeEndpoint || '').replace(/\/+$/, '');
  var PF_SECRET = DATA.printforgeSecret || '';
  var PF_ENABLED = !!(PF_BASE && PF_SECRET);

  // Once PrintForge fails (5xx, network) we back off for the rest of the session.
  var pfBackoff = false;
  // Pending requests dedup'd by combo key — concurrent picks of the same combo
  // wait on the same in-flight request.
  var pfInflight = {};

  if (FORMATS.length === 0 || COLORS.length === 0 || CONSTRUCTIONS.length === 0) return;

  function fmtEUR(n){ return Number.isInteger(n) ? n+' €' : n.toString().replace('.', ',')+' €'; }
  function shade(hex, amount){
    var h = (hex||'#888').replace('#','');
    var num = parseInt(h, 16);
    var r=(num>>16)&0xff, g=(num>>8)&0xff, b=num&0xff;
    var c = function(v){ return Math.max(0, Math.min(255, Math.round(v))); };
    if (amount>=0){ r=c(r+(255-r)*amount); g=c(g+(255-g)*amount); b=c(b+(255-b)*amount); }
    else { r=c(r*(1+amount)); g=c(g*(1+amount)); b=c(b*(1+amount)); }
    return 'rgb('+r+','+g+','+b+')';
  }
  function resolveImg(url){
    if (!url) return '';
    if (url.indexOf('shopify://shop_images/') === 0) return CDN + url.substring('shopify://shop_images/'.length);
    return url;
  }
  function byId(arr, id){ for (var i=0;i<arr.length;i++) if (arr[i].id===id) return arr[i]; return null; }
  function colorsTop(){ return COLORS.filter(function(c){ return c.usableAs!=='bottom'; }); }
  function colorsBottom(){ return COLORS.filter(function(c){ return c.usableAs!=='top'; }); }
  function comboKey(format, topColor, botColor){ return format.key+'|'+topColor.key+'|'+botColor.key; }

  /* ───────── sessionStorage cache (best-effort) ───────── */
  var SESSION_PREFIX = 'dn_combo_';
  function sessionGet(k){
    try { return window.sessionStorage.getItem(SESSION_PREFIX+k); }
    catch(e){ return null; }
  }
  function sessionSet(k, v){
    try { window.sessionStorage.setItem(SESSION_PREFIX+k, v); }
    catch(e){ /* quota or disabled — ignore */ }
  }

  /* ───────── PrintForge call ───────── */
  function callPrintForge(format, topColor, botColor){
    if (!PF_ENABLED || pfBackoff) return Promise.resolve(null);
    var key = comboKey(format, topColor, botColor);
    if (pfInflight[key]) return pfInflight[key];

    var payload = {
      format: format.key,
      top_key: topColor.key,
      top_hex: topColor.hex,
      top_name: topColor.name,
      bot_key: botColor.key,
      bot_hex: botColor.hex,
      bot_name: botColor.name
    };
    var controller = (typeof AbortController !== 'undefined') ? new AbortController() : null;
    var timeoutId = setTimeout(function(){ if (controller) controller.abort(); }, 30000);

    var p = fetch(PF_BASE + '/ensure-photo', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-deconova-secret': PF_SECRET },
      body: JSON.stringify(payload),
      mode: 'cors',
      credentials: 'omit',
      signal: controller ? controller.signal : undefined
    }).then(function(r){
      clearTimeout(timeoutId);
      if (!r.ok) {
        // 5xx → back off for the rest of the session (server in trouble)
        if (r.status >= 500) pfBackoff = true;
        return null;
      }
      return r.json();
    }).then(function(data){
      if (!data) return null;
      if (data.status === 'ready' && data.url) {
        sessionSet(key, data.url);
        return data.url;
      }
      // invalid_combo / rate_limited / quota_exceeded / no_source / error → null
      return null;
    }).catch(function(err){
      clearTimeout(timeoutId);
      console.warn('[cfg] PrintForge call failed:', err && err.message || err);
      pfBackoff = true;
      return null;
    }).finally(function(){
      delete pfInflight[key];
    });

    pfInflight[key] = p;
    return p;
  }

  /* ───────── state ───────── */
  var defConstr = CONSTRUCTIONS.find(function(c){return c.isDefault;}) || CONSTRUCTIONS[0];
  var ct = colorsTop()[0], cb = colorsBottom().find(function(c){return c.id!==ct.id;}) || colorsBottom()[0];
  var state = { format: FORMATS[0].id, construction: defConstr.id, colorTop: ct.id, colorBottom: cb.id };

  var els = {
    formats: root.querySelectorAll('[data-cfg-format]'),
    constrs: root.querySelectorAll('[data-cfg-constr]'),
    swTop: root.querySelectorAll('[data-cfg-color="top"]'),
    swBot: root.querySelectorAll('[data-cfg-color="bottom"]'),
    labelTop: root.querySelector('[data-cfg-color-label="top"]'),
    labelBot: root.querySelector('[data-cfg-color-label="bottom"]'),
    dotTop: root.querySelector('[data-cfg-color-dot="top"]'),
    dotBot: root.querySelector('[data-cfg-color-dot="bottom"]'),
    stage: root.querySelector('[data-cfg-stage]'),
    stageInner: root.querySelector('[data-cfg-stage-inner]'),
    badgeName: root.querySelector('[data-cfg-badge-name]'),
    badgeDims: root.querySelector('[data-cfg-badge-dims]'),
    lineFormat: root.querySelector('[data-cfg-line="format"]'),
    lineConstr: root.querySelector('[data-cfg-line="constr"]'),
    lineTopName: root.querySelector('[data-cfg-line="top-name"]'),
    lineBotName: root.querySelector('[data-cfg-line="bot-name"]'),
    lineTopDot: root.querySelector('[data-cfg-line="top-dot"]'),
    lineBotDot: root.querySelector('[data-cfg-line="bot-dot"]'),
    valFormat: root.querySelector('[data-cfg-val="format"]'),
    valConstr: root.querySelector('[data-cfg-val="constr"]'),
    total: root.querySelector('[data-cfg-total]'),
    cta: root.querySelector('[data-cfg-cta]'),
    ctaText: root.querySelector('[data-cfg-cta-text]'),
    stickyBar: root.querySelector('[data-cfg-stickybar]'),
    stickyTotal: root.querySelector('[data-cfg-stickybar-total]'),
    stickyCta: root.querySelector('[data-cfg-stickybar-cta]'),
    stickyThumb: root.querySelector('[data-cfg-stickybar-thumb]')
  };

  function escapeAttr(s){ return String(s||'').replace(/[&<>"']/g, function(c){return {'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c];}); }

  function renderBoxSVG(format, topHex, botHex, constrId){
    var p = format.proportions || {w:130,h:130,d:130};
    var scale = 210 / Math.max(p.w, p.h*1.35, p.d*1.35);
    var fw=p.w*scale, fh=p.h*scale, dd=p.d*scale;
    var dx=dd*0.42, dy=-dd*0.30;
    var cx=220, cy=220;
    var ox=cx-(fw+dx)/2, oy=cy+fh/2-dy/2;
    var plinthH=Math.max(Math.min(fh*0.24,30),16);
    var topMid=topHex, topRight=shade(topHex,-0.28), topRoof=shade(topHex,0.20), topRoofIn=shade(topHex,-0.40);
    var botMid=botHex, botRight=shade(botHex,-0.32);
    var FBL=[ox,oy], FBR=[ox+fw,oy], FTR=[ox+fw,oy-fh], FTL=[ox,oy-fh];
    var BTR=[ox+fw+dx,oy-fh+dy], BTL=[ox+dx,oy-fh+dy], BBR=[ox+fw+dx,oy+dy];
    var PFR=[ox+fw,oy-plinthH], PRR=[ox+fw+dx,oy-plinthH+dy];
    var ridgeW=8.5, nRidges=Math.max(8, Math.round(fw/ridgeW)), stripeW=fw/nRidges;
    var topMidX=ox+fw/2+dx/2, topMidY=oy-fh+dy/2;
    var openRx=fw*0.32, openRy2=Math.min(dd*0.18, fw*0.10);
    var pts=function(a){ return a.map(function(p){return p.join(',');}).join(' '); };
    var stripes='';
    for (var i=0; i<nRidges; i++) {
      stripes += '<rect x="'+(ox+i*stripeW+stripeW*0.5)+'" y="'+(oy-fh)+'" width="'+(stripeW*0.42)+'" height="'+fh+'" fill="url(#stripe-'+(i%2)+')"/>';
    }
    var magnet = constrId === (CONSTRUCTIONS.find(function(c){return /aiman/i.test(c.name||c.id);})||{}).id ?
      '<g><circle cx="'+(ox+fw*0.18)+'" cy="'+(oy-plinthH)+'" r="2.2" fill="var(--dn-accent)"/><circle cx="'+(ox+fw*0.82)+'" cy="'+(oy-plinthH)+'" r="2.2" fill="var(--dn-accent)"/></g>' : '';
    return [
      '<svg viewBox="0 0 440 360" class="cfg-box-svg" xmlns="http://www.w3.org/2000/svg" aria-label="Aperçu '+escapeAttr(format.name)+'">',
        '<defs>',
          '<radialGradient id="cfg-sh" cx="50%" cy="50%" r="50%"><stop offset="0%" stop-color="rgba(26,26,26,.32)"/><stop offset="60%" stop-color="rgba(26,26,26,.10)"/><stop offset="100%" stop-color="rgba(26,26,26,0)"/></radialGradient>',
          '<linearGradient id="cfg-roof" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stop-color="'+topRoof+'"/><stop offset="100%" stop-color="'+shade(topRoof,-0.10)+'"/></linearGradient>',
          '<clipPath id="cp-front"><polygon points="'+pts([FBL,FBR,FTR,FTL])+'"/></clipPath>',
          '<linearGradient id="stripe-0" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stop-color="rgba(0,0,0,.18)"/><stop offset="100%" stop-color="rgba(0,0,0,.10)"/></linearGradient>',
          '<linearGradient id="stripe-1" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stop-color="rgba(0,0,0,.14)"/><stop offset="100%" stop-color="rgba(0,0,0,.06)"/></linearGradient>',
        '</defs>',
        '<ellipse cx="'+(cx+dx*0.3)+'" cy="'+(oy+18)+'" rx="'+((fw+dx)*0.52)+'" ry="'+Math.min(14,fw*0.06)+'" fill="url(#cfg-sh)"/>',
        '<polygon points="'+pts([FTL,FTR,BTR,BTL])+'" fill="url(#cfg-roof)"/>',
        '<ellipse cx="'+topMidX+'" cy="'+topMidY+'" rx="'+openRx+'" ry="'+openRy2+'" fill="'+topRoofIn+'"/>',
        '<path d="M '+(topMidX-openRx*0.55)+' '+(topMidY-openRy2*0.2)+' C '+(topMidX-openRx*0.5)+' '+(topMidY-openRy2*2.6)+', '+(topMidX-openRx*0.1)+' '+(topMidY-openRy2*3.4)+', '+(topMidX+openRx*0.1)+' '+(topMidY-openRy2*2.8)+' C '+(topMidX+openRx*0.3)+' '+(topMidY-openRy2*2.0)+', '+(topMidX+openRx*0.5)+' '+(topMidY-openRy2*1.0)+', '+(topMidX+openRx*0.55)+' '+(topMidY-openRy2*0.1)+' Z" fill="#FAFAF6" stroke="rgba(26,26,26,.06)"/>',
        '<polygon points="'+pts([FTR,BTR,PRR,PFR])+'" fill="'+topRight+'"/>',
        '<polygon points="'+pts([PFR,PRR,BBR,FBR])+'" fill="'+botRight+'"/>',
        '<rect x="'+ox+'" y="'+(oy-fh)+'" width="'+fw+'" height="'+(fh-plinthH)+'" fill="'+topMid+'"/>',
        '<rect x="'+ox+'" y="'+(oy-plinthH)+'" width="'+fw+'" height="'+plinthH+'" fill="'+botMid+'"/>',
        '<g clip-path="url(#cp-front)">'+stripes+'<rect x="'+ox+'" y="'+(oy-plinthH-0.5)+'" width="'+fw+'" height="1" fill="rgba(26,26,26,.18)"/></g>',
        '<line x1="'+ox+'" y1="'+(oy-fh)+'" x2="'+(ox+fw)+'" y2="'+(oy-fh)+'" stroke="rgba(255,255,255,.18)"/>',
        magnet,
      '</svg>'
    ].join('');
  }

  // Sync lookup chain: returns { kind: 'photo'|'svg', url?, html }
  function lookupVisual(format, topColor, botColor, constrId){
    var fk = format.key, tk = topColor.key, bk = botColor.key;
    var exact = VISUALS.find(function(v){ return v.f===fk && v.t===tk && v.b===bk; });
    if (exact && exact.img) {
      var url = resolveImg(exact.img);
      return { kind: 'photo', url: url, html: '<img class="cfg-box-photo" src="'+url+'" alt="" loading="lazy">' };
    }
    var topOnly = VISUALS.find(function(v){ return v.f===fk && v.t===tk && (!v.b || v.b==='*'); });
    if (topOnly && topOnly.img) {
      var url2 = resolveImg(topOnly.img);
      return { kind: 'photo', url: url2, html: '<img class="cfg-box-photo" src="'+url2+'" alt="" loading="lazy">' };
    }
    var generic = VISUALS.find(function(v){ return v.f===fk && (!v.t || v.t==='*'); });
    if (generic && generic.img) {
      var url3 = resolveImg(generic.img);
      return { kind: 'photo', url: url3, html: '<img class="cfg-box-photo" src="'+url3+'" alt="" loading="lazy">' };
    }
    // Session cache
    var skey = comboKey(format, topColor, botColor);
    var cached = sessionGet(skey);
    if (cached) {
      var url4 = resolveImg(cached);
      return { kind: 'photo', url: url4, html: '<img class="cfg-box-photo" src="'+url4+'" alt="" loading="lazy">' };
    }
    return { kind: 'svg', html: renderBoxSVG(format, topColor.hex, botColor.hex, constrId) };
  }

  /* ───────── Generating overlay UI ─────────
     Small badge in the bottom-right of the stage, appears after a 600ms grace
     period (avoids flashing when PrintForge returns fast from cache). */
  function ensureOverlay(stageEl){
    var ov = stageEl.querySelector('[data-cfg-gen-overlay]');
    if (ov) return ov;
    ov = document.createElement('div');
    ov.setAttribute('data-cfg-gen-overlay', '');
    ov.style.cssText = [
      'position:absolute', 'right:12px', 'bottom:12px',
      'display:none', 'align-items:center', 'gap:8px',
      'padding:8px 12px', 'border-radius:999px',
      'background:rgba(26,26,26,0.78)', 'color:#fff',
      'font:500 12px/1.2 system-ui,sans-serif',
      'letter-spacing:0.02em', 'backdrop-filter:blur(8px)',
      '-webkit-backdrop-filter:blur(8px)',
      'pointer-events:none', 'z-index:5',
      'box-shadow:0 4px 16px rgba(0,0,0,.18)'
    ].join(';');
    ov.innerHTML = [
      '<span style="display:inline-block;width:10px;height:10px;border-radius:50%;',
      'border:2px solid rgba(255,255,255,.35);border-top-color:#fff;',
      'animation:cfgGenSpin .8s linear infinite"></span>',
      '<span>Aperçu en cours de génération…</span>'
    ].join('');
    // Inject the keyframe once (idempotent)
    if (!document.getElementById('cfg-gen-keyframes')) {
      var st = document.createElement('style');
      st.id = 'cfg-gen-keyframes';
      st.textContent = '@keyframes cfgGenSpin{to{transform:rotate(360deg)}}';
      document.head.appendChild(st);
    }
    if (getComputedStyle(stageEl).position === 'static') stageEl.style.position = 'relative';
    stageEl.appendChild(ov);
    return ov;
  }
  function showOverlay(stageEl){ var ov = ensureOverlay(stageEl); ov.style.display = 'inline-flex'; }
  function hideOverlay(stageEl){ var ov = stageEl.querySelector('[data-cfg-gen-overlay]'); if (ov) ov.style.display = 'none'; }

  /* setStage: token-guarded async swap.
     - Always paints something synchronously (photo from visuals_data/session, or SVG).
     - If we painted SVG and PrintForge is enabled, fire an async call and swap on success.
     - Old image stays visible during photo preload; SVG stays visible during PrintForge call. */
  function setStage(el, format, colTop, colBot, constrId) {
    var meta = el._cfgMeta || (el._cfgMeta = {});
    var formatChanged = meta.formatId !== format.id;
    meta.formatId = format.id;
    var token = (meta.token || 0) + 1;
    meta.token = token;
    var stageEl = els.stage;

    function applyHTML(html){
      if (meta.token !== token) return;
      var match = html.match(/^<img[^>]+src="([^"]+)"/);
      var url = match ? match[1] : null;
      if (url && url === meta.url) return;
      meta.url = url;
      el.innerHTML = html;
      if (formatChanged) { el.style.animation='none'; void el.offsetWidth; el.style.animation=''; }
    }
    function applyURL(url){
      if (meta.token !== token) return;
      if (url === meta.url) return;
      meta.url = url;
      el.innerHTML = '<img class="cfg-box-photo" src="'+url+'" alt="" loading="lazy">';
      if (formatChanged) { el.style.animation='none'; void el.offsetWidth; el.style.animation=''; }
    }

    var visual = lookupVisual(format, colTop, colBot, constrId);
    if (visual.kind === 'photo') {
      // Preload + decode to avoid the half-rendered flash
      var img = new Image();
      img.src = visual.url;
      var done = function(){ applyHTML(visual.html); hideOverlay(stageEl); };
      if (typeof img.decode === 'function') img.decode().then(done).catch(done);
      else if (img.complete) done();
      else { img.onload = done; img.onerror = done; }
      return;
    }

    // SVG fallback path → paint SVG immediately, try PrintForge in background
    applyHTML(visual.html);

    if (!PF_ENABLED || pfBackoff || stageEl !== els.stage) return; // sticky thumb skips live-gen

    var overlayTimer = setTimeout(function(){
      if (meta.token === token) showOverlay(stageEl);
    }, 600);

    callPrintForge(format, colTop, colBot).then(function(url){
      clearTimeout(overlayTimer);
      if (meta.token !== token) { hideOverlay(stageEl); return; }
      if (url) {
        // Preload before swap
        var img2 = new Image();
        var resolvedUrl = resolveImg(url);
        img2.src = resolvedUrl;
        var doSwap = function(){
          if (meta.token !== token) return;
          applyURL(resolvedUrl);
          hideOverlay(stageEl);
        };
        if (typeof img2.decode === 'function') img2.decode().then(doSwap).catch(doSwap);
        else if (img2.complete) doSwap();
        else { img2.onload = doSwap; img2.onerror = doSwap; }
      } else {
        hideOverlay(stageEl);
      }
    });
  }

  var flashTimers = {};
  function flashLine(el){
    if (!el) return;
    el.setAttribute('data-changed', 'true');
    var key = el.dataset.cfgLine||el.dataset.cfgVal||'k';
    clearTimeout(flashTimers[key]);
    flashTimers[key] = setTimeout(function(){ el.removeAttribute('data-changed'); }, 420);
  }
  var pulseTimer = null, lastTotal = null;
  function pulseTotal(){
    if (!SHOW_PULSE || !els.total) return;
    els.total.setAttribute('data-pulse','true');
    clearTimeout(pulseTimer);
    pulseTimer = setTimeout(function(){ els.total.removeAttribute('data-pulse'); }, 380);
  }

  function render(){
    var format = byId(FORMATS, state.format) || FORMATS[0];
    var constr = byId(CONSTRUCTIONS, state.construction) || CONSTRUCTIONS[0];
    var colTop = byId(COLORS, state.colorTop) || COLORS[0];
    var colBot = byId(COLORS, state.colorBottom) || COLORS[0];
    var total = (format.price||0) + (constr.extra||0);

    els.formats.forEach(function(b){ b.setAttribute('aria-pressed', b.dataset.cfgFormat===state.format ? 'true' : 'false'); });
    els.constrs.forEach(function(b){ b.setAttribute('aria-pressed', b.dataset.cfgConstr===state.construction ? 'true' : 'false'); });
    els.swTop.forEach(function(b){ b.setAttribute('aria-pressed', b.dataset.cfgColorId===state.colorTop ? 'true' : 'false'); });
    els.swBot.forEach(function(b){ b.setAttribute('aria-pressed', b.dataset.cfgColorId===state.colorBottom ? 'true' : 'false'); });

    if (els.labelTop) els.labelTop.textContent = colTop.name;
    if (els.labelBot) els.labelBot.textContent = colBot.name;
    if (els.dotTop) els.dotTop.style.setProperty('--cfg-tab-dot-color', colTop.hex);
    if (els.dotBot) els.dotBot.style.setProperty('--cfg-tab-dot-color', colBot.hex);

    if (els.stageInner) setStage(els.stageInner, format, colTop, colBot, state.construction);
    if (els.badgeName) els.badgeName.textContent = format.name;
    if (els.badgeDims) els.badgeDims.textContent = format.dims || '';

    if (els.lineFormat) els.lineFormat.textContent = format.name;
    if (els.valFormat) els.valFormat.textContent = fmtEUR(format.price||0);
    if (els.lineConstr) els.lineConstr.textContent = constr.name;
    if (els.valConstr) els.valConstr.textContent = constr.extraLabel || (constr.extra>0 ? '+'+fmtEUR(constr.extra) : 'incl.');
    if (els.lineTopName) els.lineTopName.textContent = colTop.name;
    if (els.lineBotName) els.lineBotName.textContent = colBot.name;
    if (els.lineTopDot) els.lineTopDot.style.setProperty('--cfg-tab-dot-color', colTop.hex);
    if (els.lineBotDot) els.lineBotDot.style.setProperty('--cfg-tab-dot-color', colBot.hex);

    if (els.total) els.total.textContent = fmtEUR(total);
    if (lastTotal !== null && lastTotal !== total) pulseTotal();
    lastTotal = total;

    var ready = !!(format && constr && colTop && colBot && format.variantId);
    if (els.cta) els.cta.disabled = !ready;
    if (els.ctaText) els.ctaText.textContent = ready ? CTA_COMPLETE.replace('%price%', fmtEUR(total)) : CTA_INCOMPLETE;

    if (els.stickyTotal) els.stickyTotal.textContent = fmtEUR(total);
    if (els.stickyCta) els.stickyCta.disabled = !ready;
    if (els.stickyThumb) setStage(els.stickyThumb, format, colTop, colBot, state.construction);

    root.dataset.cfgVariantId = format.variantId || '';
    root.dataset.cfgFormatName = format.name || '';
    root.dataset.cfgConstrName = constr.name || '';
    root.dataset.cfgTopName = colTop.name || '';
    root.dataset.cfgBotName = colBot.name || '';
  }

  els.formats.forEach(function(b){ b.addEventListener('click', function(){ if (state.format!==b.dataset.cfgFormat){ state.format = b.dataset.cfgFormat; flashLine(els.valFormat); render(); } }); });
  els.constrs.forEach(function(b){ b.addEventListener('click', function(){ if (state.construction!==b.dataset.cfgConstr){ state.construction = b.dataset.cfgConstr; flashLine(els.valConstr); render(); } }); });
  els.swTop.forEach(function(b){ b.addEventListener('click', function(){ if (state.colorTop!==b.dataset.cfgColorId){ state.colorTop = b.dataset.cfgColorId; flashLine(els.lineTopName && els.lineTopName.parentElement); render(); } }); });
  els.swBot.forEach(function(b){ b.addEventListener('click', function(){ if (state.colorBottom!==b.dataset.cfgColorId){ state.colorBottom = b.dataset.cfgColorId; flashLine(els.lineBotName && els.lineBotName.parentElement); render(); } }); });

  function submitCart(){
    var variantId = root.dataset.cfgVariantId;
    if (!variantId){ console.warn('[cfg] no variant id'); return; }
    var payload = { id: variantId, quantity: 1, properties: { 'Format': root.dataset.cfgFormatName, 'Construction': root.dataset.cfgConstrName, 'Couleur top': root.dataset.cfgTopName, 'Couleur bottom': root.dataset.cfgBotName } };
    if (els.cta) els.cta.disabled = true;
    if (els.stickyCta) els.stickyCta.disabled = true;
    fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/javascript' }, body: JSON.stringify(payload), credentials: 'same-origin' })
    .then(function(r){ if (!r.ok) throw new Error('cart/add failed '+r.status); return r.json(); })
    .then(function(item){
      document.dispatchEvent(new CustomEvent('cart:refresh', { detail: item }));
      document.dispatchEvent(new CustomEvent('cart:open', { detail: item }));
      var drawerTrigger = document.querySelector('[data-cart-drawer-trigger],[data-action="open-cart"],a[href="/cart"]');
      if (drawerTrigger && typeof drawerTrigger.click === 'function') {
        setTimeout(function(){ drawerTrigger.click(); }, 60);
      } else {
        setTimeout(function(){ window.location.href = '/cart'; }, 250);
      }
    })
    .catch(function(err){ console.error('[cfg] cart submit error', err); window.location.href = '/cart'; })
    .finally(function(){ if (els.cta) els.cta.disabled = false; if (els.stickyCta) els.stickyCta.disabled = false; });
  }
  if (els.cta) els.cta.addEventListener('click', function(e){ e.preventDefault(); submitCart(); });
  if (els.stickyCta) els.stickyCta.addEventListener('click', function(e){ e.preventDefault(); submitCart(); });

  if (els.stickyBar) {
    var stage = els.stage;
    var footer = document.querySelector('.dn-tt-footer, footer.dn-tt-footer');
    if ('IntersectionObserver' in window) {
      var visState = { stagePast: false, footerVisible: false };
      function applyVis(){
        var show = visState.stagePast && !visState.footerVisible;
        els.stickyBar.classList.toggle('is-visible', show);
        els.stickyBar.classList.toggle('is-footer-visible', visState.footerVisible);
      }
      if (stage) {
        var ioStage = new IntersectionObserver(function(entries){
          entries.forEach(function(e){ visState.stagePast = !e.isIntersecting && e.boundingClientRect.bottom < 0; applyVis(); });
        }, { threshold: 0 });
        ioStage.observe(stage);
      }
      if (footer) {
        var ioFooter = new IntersectionObserver(function(entries){
          entries.forEach(function(e){ visState.footerVisible = e.isIntersecting; applyVis(); });
        }, { threshold: 0.01 });
        ioFooter.observe(footer);
      }
    }
  }

  document.querySelectorAll('a[href="#composer"], a[href$="#composer"]').forEach(function(a){
    a.addEventListener('click', function(e){
      var target = document.getElementById('composer');
      if (!target) return;
      e.preventDefault();
      var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      var top = target.getBoundingClientRect().top + window.pageYOffset - 16;
      window.scrollTo({ top: top, behavior: reduce ? 'auto' : 'smooth' });
      if (history.replaceState) history.replaceState(null, '', '#composer');
    });
  });

  render();
})();
